import React from 'react';
import {Table} from '@/web-components';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import Util from '../../../../forecast-app/shared/util/util';
import {ProjectStatusColor, TextColumn} from './ProjectPortfolioReport.styled';
import Person from '../../../../forecast-app/shared/components/person/person';
import {BUDGET_TYPE, PROGRAM_BUDGET_TYPE} from '../../../../constants';
import TooltipContainer from '../../../../forecast-app/shared/components/tooltips/tooltip_container';
import {ProjectContact} from './ProjectContact.styled';
import {ProjectPortfolioReportTooltips} from '../ProjectPortfolioReportTooltips';
import LabelGroup from '../../../../forecast-app/shared/components/labels/label_group';
import {CurrencyColumn} from '../CurrencyColumn';
import {PercentageColumn} from '../PercentageColumn';
import {HoursColumn} from '../HoursColumn';
import {profilePicSrc} from '../../../../directApi';
import CompanySetupUtil from '../../../../forecast-app/shared/util/CompanySetupUtil';
import ProjectUtil from '../../../../forecast-app/shared/util/ProjectUtil';
import {OpportunityTooltip} from '../OpportunityTooltip';
import {isProjectOpportunity, stageLabel} from '../../../../forecast-app/shared/util/projectStageUtil';

export const renderStatus = status => {
	if (status) {
		return <ProjectStatusColor style={{backgroundColor: status.color}} />;
	} else {
		return null;
	}
};

export const renderDescription = status => {
	let text = '';

	if (status && status.description) {
		try {
			const description = JSON.parse(status.description);
			text = description.blocks ? description.blocks.map(block => block.text).join('\n') : text;
		} catch (e) {
			text = status.description;
		}
	}

	return (
		<TooltipContainer infoText={text}>
			<TextColumn
				style={{
					WebkitLineClamp: '2',
					WebkitBoxOrient: 'vertical',
					whiteSpace: 'break-spaces',
					display: '-webkit-box',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					justifyContent: 'center',
					lineHeight: '1.5',
				}}
			>
				{text}
			</TextColumn>
		</TooltipContainer>
	);
};

export const renderStage = stage => {
	return <TextColumn>{stageLabel(stage, useIntl())}</TextColumn>;
};

export const renderRateCard = rateCard => {
	return <TextColumn>{rateCard && rateCard.name}</TextColumn>;
};

export const renderClient = client => {
	return <TextColumn>{client && client.name}</TextColumn>;
};

export const renderPriorityLevel = priorityLevel => {
	return <TextColumn>{priorityLevel && priorityLevel.name}</TextColumn>;
};

export const renderDate = date => {
	let dateString = '';
	if (date) {
		dateString = `${useIntl().formatDate(date.toDate(), {year: 'numeric', month: 'short', day: '2-digit'})}`;
	}
	return dateString;
};

export const renderOwners = people => {
	let contactPersons = [];
	if (people && people.edges) {
		contactPersons = people.edges
			.map(edge => edge.node)
			.filter(projectPerson => projectPerson.isContactPerson)
			.map(projectPerson => projectPerson.person);
	}

	return (
		<>
			{contactPersons.map((contactPerson, index) => (
				<ProjectContact key={index}>
					<TooltipContainer translatedMessage={true} infoText={contactPerson.fullName} timer={5 * 60 * 1000}>
						<Person
							name={contactPerson.fullName}
							imageSrc={profilePicSrc(contactPerson.profilePictureId)}
							showName={contactPersons.length === 1}
							showRole={false}
							hideTitle={true}
						/>
					</TooltipContainer>
				</ProjectContact>
			))}
		</>
	);
};

export const BudgetTypeColumn = ({projectData}) => {
	let label = '';
	const intl = useIntl();
	if (projectData) {
		const {budgetType, defaultPeriodBudgetType} = projectData;
		label = Util.getBudgetTypeTranslation(budgetType, intl);
		if (budgetType === BUDGET_TYPE.RETAINER && defaultPeriodBudgetType) {
			label += ` (${Util.getPeriodBudgetTypeTranslation(defaultPeriodBudgetType, intl)})`;
		}
	}
	return <Table.Column>{label}</Table.Column>;
};

export const renderLabels = value => {
	if (value) {
		const labels = value.edges.map(e => e.node);
		return <LabelGroup labels={labels} noWrap={true} />;
	} else {
		return null;
	}
};

export const getProjectData = rowData => {
	const projectData = rowData.project ? rowData.project : {};

	projectData.startDate = Util.CreateNonUtcMomentDate(
		projectData.projectStartYear,
		projectData.projectStartMonth,
		projectData.projectStartDay
	);
	projectData.endDate = Util.CreateNonUtcMomentDate(
		projectData.projectEndYear,
		projectData.projectEndMonth,
		projectData.projectEndDay
	);
	return projectData;
};

export const getPhaseData = rowData => {
	const phaseData = rowData.phase ? {...rowData.phase} : {};

	if (rowData.phase) {
		phaseData.startDate = Util.CreateNonUtcMomentDate(phaseData.startYear, phaseData.startMonth, phaseData.startDay);
		phaseData.endDate = Util.CreateNonUtcMomentDate(phaseData.deadlineYear, phaseData.deadlineMonth, phaseData.deadlineDay);
	}
	return phaseData;
};

export const OpportunityProjectGuardColumn = ({isOpportunityProject, greyedOut, children}) => {
	if (isOpportunityProject) {
		return (
			<Table.Column align={'right'} usePadding greyedOut={greyedOut}>
				<OpportunityTooltip />
			</Table.Column>
		);
	}

	return children;
};

const ProjectPortfolioPeriodRow = ({
	financialNumbers,
	rowData,
	tableColumnProps,
	headerColumn,
	canExpand,
	useWinProbability = false,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const projectData = getProjectData(rowData);
	const phaseData = getPhaseData(rowData);
	const isOpportunityProject = isProjectOpportunity(projectData);
	const showWinProbabilityTooltip = isOpportunityProject && useWinProbability;

	const isFixedPriceProject = () => {
		return [BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2].includes(projectData.budgetType);
	};

	const getTotalRevRecWithTotalUnallocatedRevenue = () => {
		// Used for totals row
		return financialNumbers.totalRevenueRecognition + rowData.unallocatedRevenueTotal;
	};

	const getTotalForecastedRevenueWithUnallocated = () => {
		// In financial Service recognitionForecastRevenue is calculated by adding recognitionLockedRevenue + recognitionOpenRevenue
		// which are adjusted if the win probability is applied. We are not using totalRevenueRecognition because it's
		// already used by two other sections (Forecasted and Over/Under Servicing). The ideal solution would be to introduce another
		// field to represent the other two fields/columns and use totalRevenueRecognition for the revenue recognition sections.
		return financialNumbers.recognitionForecastRevenue + rowData.unallocatedRevenueTotal;
	};

	if (canExpand !== undefined) {
		tableColumnProps.canExpand = canExpand;
	}

	const showProgramBudgetValueAsTotalRevRec =
		!!rowData.program && rowData.program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE;
	const showZerosWhereApplicable = !rowData.isFooter;
	const isFixedPriceOrFixedPriceRetainerProject = ProjectUtil.isFixedPriceProject(projectData);

	return (
		<Table.Row {...tableColumnProps}>
			<Table.Column>{headerColumn}</Table.Column>
			{/* Project Overview */}
			<Table.Column>{renderPriorityLevel(projectData.priorityLevel)}</Table.Column>
			<PercentageColumn value={projectData.baselineWinChance} />
			<Table.Column>{renderDate(phaseData.startDate ?? projectData.startDate)}</Table.Column>
			<Table.Column>{renderDate(phaseData.endDate ?? projectData.endDate)}</Table.Column>
			<Table.Column>{renderClient(projectData.client)}</Table.Column>
			<Table.Column>{renderStatus(projectData.currentProjectStatus)}</Table.Column>
			<Table.Column>{renderDescription(projectData.currentProjectStatus)}</Table.Column>
			<Table.Column>{renderOwners(projectData.projectPersons)}</Table.Column>
			<Table.Column>{renderStage(projectData.stage)}</Table.Column>
			<BudgetTypeColumn projectData={projectData} />
			{CompanySetupUtil.hasFinance() ? (
				<Table.Column>{renderRateCard(projectData.rateCard)}</Table.Column>
			) : (
				<CurrencyColumn value={projectData.flatRate} currency={rowData.currency} />
			)}
			<PercentageColumn value={(projectData.completion || phaseData.progress) / 100} />
			{/* Project Details */}
			<HoursColumn value={financialNumbers.baselineMinutes} showWinProbabilityTooltip={showWinProbabilityTooltip} />
			<HoursColumn value={financialNumbers.scopeApprovedMinutes} showWinProbabilityTooltip={showWinProbabilityTooltip} />
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<HoursColumn value={financialNumbers.registeredMinutes} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<HoursColumn value={financialNumbers.forecastTimeToComplete} />
			</OpportunityProjectGuardColumn>
			<HoursColumn value={financialNumbers.totalTimeAtCompletion} showWinProbabilityTooltip={showWinProbabilityTooltip} />
			{/* Baseline */}
			<CurrencyColumn
				value={financialNumbers.baselineRevenue}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={
					projectData.useBaseline &&
					showWinProbabilityTooltip &&
					ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
				}
			/>
			<CurrencyColumn
				value={financialNumbers.baselineTimeAndExpenses}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={
					projectData.useBaseline &&
					showWinProbabilityTooltip &&
					ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
				}
			/>
			<CurrencyColumn
				value={financialNumbers.baselineCost}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={
					projectData.useBaseline &&
					showWinProbabilityTooltip &&
					ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
				}
			/>
			<CurrencyColumn
				value={financialNumbers.baselineProfit}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={
					projectData.useBaseline &&
					showWinProbabilityTooltip &&
					ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
				}
			/>
			<PercentageColumn
				value={financialNumbers.baselineMargin}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={
					projectData.useBaseline &&
					showWinProbabilityTooltip &&
					ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)
				}
			/>
			{/* Planned */}
			<CurrencyColumn
				value={financialNumbers.plannedRevenue}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.billablePlannedTimeAndExpenses}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.plannedCost}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.plannedRevenueProfit}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<PercentageColumn
				value={financialNumbers.plannedRevenueMargin}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			{/* Actuals */}
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn value={financialNumbers.totalActualRevenueRecognition} currency={rowData.currency} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn value={financialNumbers.billableActualTimeAndExpenses} currency={rowData.currency} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn value={financialNumbers.actualCost} currency={rowData.currency} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn value={financialNumbers.actualRevenueProfit} currency={rowData.currency} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<PercentageColumn value={financialNumbers.actualRevenueMargin} />
			</OpportunityProjectGuardColumn>

			{/* Remaining */}
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn value={financialNumbers.totalForecastRevenueToComplete} currency={rowData.currency} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn
					value={financialNumbers.billableForecastTimeAndExpensesToComplete}
					currency={rowData.currency}
				/>
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn value={financialNumbers.forecastCostToComplete} currency={rowData.currency} />
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn
					value={financialNumbers.forecastRevenueProfitToComplete}
					showZero={showZerosWhereApplicable}
					currency={rowData.currency}
				/>
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<PercentageColumn value={financialNumbers.forecastRevenueMarginToComplete} />
			</OpportunityProjectGuardColumn>

			{/* Forecasted / Projected */}
			<CurrencyColumn
				value={
					rowData.isTotalsRow ? getTotalRevRecWithTotalUnallocatedRevenue() : financialNumbers.totalRevenueRecognition
				}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.billableTotalTimeAndExpensesAtCompletion}
				currency={rowData.currency}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.totalCostAtCompletion}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.totalRevenueProfitAtCompletion}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<PercentageColumn
				value={financialNumbers.totalRevenueMarginAtCompletion}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			{/* Invoiced */}
			<CurrencyColumn value={financialNumbers.invoiced} currency={rowData.currency} />
			<CurrencyColumn value={financialNumbers.paid} currency={rowData.currency} />
			{/* Revenue Recognition */}
			<CurrencyColumn
				value={financialNumbers.recognitionLockedRevenue}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.recognitionOpenRevenue}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={
					showProgramBudgetValueAsTotalRevRec
						? financialNumbers.programBudgetValue
						: rowData.isTotalsRow
						? getTotalForecastedRevenueWithUnallocated()
						: financialNumbers.recognitionForecastRevenue
				}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={projectData.useBaseline && showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			{/* Fixed Price */}
			<CurrencyColumn
				value={isFixedPriceProject() ? financialNumbers.totalSuggestedRevenue : null}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			{/* Actual vs Plan */}
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn
					value={financialNumbers.planVsActualRevenueRecognition}
					showZero={showZerosWhereApplicable}
					currency={rowData.currency}
				/>
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn
					value={financialNumbers.planVsActualCost}
					showZero={showZerosWhereApplicable}
					currency={rowData.currency}
				/>
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn
					value={financialNumbers.planVsActualRevenueProfit}
					showZero={showZerosWhereApplicable}
					currency={rowData.currency}
				/>
			</OpportunityProjectGuardColumn>
			<OpportunityProjectGuardColumn isOpportunityProject={isOpportunityProject}>
				<CurrencyColumn
					value={financialNumbers.planVsBillableActualTimeAndExpenses}
					showZero={showZerosWhereApplicable}
					currency={rowData.currency}
				/>
			</OpportunityProjectGuardColumn>
			{/* Forecasted vs Plan */}
			<CurrencyColumn
				value={
					financialNumbers.plannedRevenue -
					(rowData.isTotalsRow
						? getTotalRevRecWithTotalUnallocatedRevenue()
						: financialNumbers.totalRevenueRecognition)
				}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.plannedCost - financialNumbers.totalCostAtCompletion}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.plannedRevenueProfit - financialNumbers.totalRevenueProfitAtCompletion}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={
					financialNumbers.billablePlannedTimeAndExpenses - financialNumbers.billableTotalTimeAndExpensesAtCompletion
				}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			{/* Over/Under Servicing */}
			<CurrencyColumn
				value={financialNumbers.totalRevenueRecognition}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.billableTotalTimeAndExpensesAtCompletion}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.nonBillableTotalTimeAndExpensesAtCompletion}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.allTotalTimeAndExpensesAtCompletion}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={
					isFixedPriceOrFixedPriceRetainerProject
						? financialNumbers.projectedTotalRevenueVsProjectedTotalBillableValueOfService
						: '-'
				}
				showZero={isFixedPriceOrFixedPriceRetainerProject}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			<CurrencyColumn
				value={financialNumbers.projectedTotalRevenueVsProjectedTotalValueOfService}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
			{/* Invoiced vs. Actual */}
			<CurrencyColumn value={financialNumbers.invoiced} showZero={showZerosWhereApplicable} currency={rowData.currency} />
			<CurrencyColumn
				value={financialNumbers.billableActualTimeAndExpenses}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialNumbers.recognitionLockedRevenue}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialNumbers.accruedDeferred}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
			/>
			{/* Labels */}
			<Table.Column>{renderLabels(projectData.projectLabels)}</Table.Column>
			{/* Retainer period target */}
			<HoursColumn
				showWinProbabilityTooltip={showWinProbabilityTooltip}
				value={financialNumbers.retainerPeriodTargetMinutes}
			/>
			<CurrencyColumn
				value={financialNumbers.retainerPeriodTargetPrice}
				showZero={showZerosWhereApplicable}
				currency={rowData.currency}
				showGenericWinProbabilityTooltip={showWinProbabilityTooltip}
				tooltip={showWinProbabilityTooltip && ProjectPortfolioReportTooltips.GenericWinChanceTooltip(formatMessage)}
			/>
		</Table.Row>
	);
};

export default createFragmentContainer(ProjectPortfolioPeriodRow, {
	financialNumbers: graphql`
		fragment ProjectPortfolioPeriodRow_financialNumbers on FinancialNumbers {
			billableActualTimeAndExpenses
			totalActualRevenueRecognition
			actualCost
			actualRevenueProfit
			actualRevenueMargin
			billablePlannedTimeAndExpenses
			plannedRevenue
			plannedCost
			plannedRevenueProfit
			plannedRevenueMargin
			billableForecastTimeAndExpensesToComplete
			totalForecastRevenueToComplete
			forecastCostToComplete
			forecastRevenueProfitToComplete
			forecastRevenueMarginToComplete
			billableTotalTimeAndExpensesAtCompletion
			nonBillableTotalTimeAndExpensesAtCompletion
			allTotalTimeAndExpensesAtCompletion
			totalRevenueRecognition
			totalCostAtCompletion
			totalRevenueProfitAtCompletion
			totalRevenueMarginAtCompletion
			baselineRevenue
			baselineTimeAndExpenses
			baselineCost
			baselineProfit
			baselineMargin
			baselineMinutes
			planVsBillableActualTimeAndExpenses
			planVsActualCost
			planVsActualRevenueProfit
			planVsActualRevenueRecognition
			planVsTotalRevenueAtCompletion
			planVsTotalBillableTimeAndExpensesAtCompletion
			planVsTotalCostAtCompletion
			planVsTotalProfitAtCompletion
			planVsTotalRevenueProfitAtCompletion
			registeredMinutes
			forecastTimeToComplete
			totalTimeAtCompletion
			totalSuggestedRevenue
			scopeApprovedMinutes
			invoiced
			paid
			accruedDeferred
			retainerPeriodTargetMinutes
			retainerPeriodTargetPrice
			recognitionLockedRevenue
			recognitionOpenRevenue
			totalRevenueRecognition
			programBudgetValue
			projectedTotalRevenueVsProjectedTotalBillableValueOfService
			projectedTotalRevenueVsProjectedTotalValueOfService
			recognitionForecastRevenue
		}
	`,
});
